import { useSyncedRef } from "@react-hookz/web";
import { useAtomValue } from "jotai";
import { useRouter } from "next/router";
import qs from "qs";
import { useEffect } from "react";

import { useReaction } from "~/components/content/content-reaction/hooks";
import type { ReactionQueryStringPayload } from "~/components/content/content-reaction/types";
import { useLoginDialogStateHelper } from "~/components/login-dialog/hooks";
import { sendGATracking } from "~/components/tracking/google-analytics/apis";
import { sendGA4Tracking } from "~/components/tracking/google-analytics-4/apis";
import { accountAtom } from "~/lib/account";
import { useRouterReplaceWithSanitizedKeys } from "~/lib/router/hooks";

import { SanitizedUrlKeys } from "./consts";

export const useContentReactionActionRedirect = () => {
  const { handleReactionAction } = useReaction();
  const { asPath, pathname, query } = useRouter();
  const replace = useRouterReplaceWithSanitizedKeys();
  const latestHandleReactionAction = useSyncedRef(handleReactionAction);

  const { user } = useAtomValue(accountAtom);

  const { openLoginDialog } = useLoginDialogStateHelper();

  useEffect(() => {
    const parsedQueryString = qs.parse(window.location.search, {
      ignoreQueryPrefix: true,
    }) as unknown as ReactionQueryStringPayload;

    if (!parsedQueryString.action?.reaction) return;

    const { count, entityId, ...ga4CustomizedParameter } = parsedQueryString.action?.reaction ?? {};

    if (parsedQueryString.action?.reaction && !user?.uuid) {
      openLoginDialog({
        destination: window.location.href,
      });
      return;
    }

    void latestHandleReactionAction.current(
      {
        action: "Upvote",
        entityId,
      },
      () => {
        sendGATracking({
          action: "Article Reaction/Like/Click/Success",
          category: "Article Reaction",
          label: `${count}`,
        });
        sendGA4Tracking({
          action: "sys",
          category: "page",
          customized_parameters: {
            ...ga4CustomizedParameter,
            action_type: "like",
            reaction_count: `${count}`,
          },
          subcategory: "reaction",
        });
        void replace(
          SanitizedUrlKeys,
          { pathname, query },
          {
            shallow: true,
          },
        );
      },
    );
  }, [asPath, pathname, query, replace, openLoginDialog, user?.uuid, latestHandleReactionAction]);

  return null;
};
